import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { diffSnapshots, groupVersionsByDay, type HistoryDay, type HistoryKind } from "@/lib/page-history";
import {
  validatePageForPublish,
  validateSlug,
  type PublishCheck,
  type PublishIssue,
} from "@/lib/page-validation";


export type SitePage = {
  slug: string;
  title: string;
  meta_description: string;
  hero_eyebrow: string;
  hero_title: string;
  hero_lead: string;
  hero_image_url: string | null;
  body_md: string;
  use_custom_body: boolean;
  is_published: boolean;
  extra: Record<string, any>;
  updated_at: string;
};

export type SitePageAdmin = SitePage & {
  draft: Partial<SitePage> | null;
};

/** Fields admins can edit and stage in a draft. */
const DRAFT_FIELDS = [
  "title",
  "meta_description",
  "hero_eyebrow",
  "hero_title",
  "hero_lead",
  "hero_image_url",
  "body_md",
  "use_custom_body",
  "extra",
] as const;

function pickDraft(input: Partial<SitePage>): Partial<SitePage> {
  const out: Record<string, any> = {};
  for (const k of DRAFT_FIELDS) {
    if (k in input) out[k] = (input as any)[k];
  }
  return out;
}

function mergeDraft(row: any): SitePage {
  const draft = (row?.draft ?? {}) as Partial<SitePage>;
  const { draft: _d, ...live } = row ?? {};
  return { ...(live as SitePage), ...draft };
}

export const getPage = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data }): Promise<SitePage | null> => {
    const sb = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data: row } = await sb
      .from("site_pages")
      .select("*")
      .eq("slug", data.slug)
      .is("deleted_at", null)
      .maybeSingle();
    if (!row) return null;
    // Public read: never expose the draft. Strip it.
    const { draft: _drop, ...live } = row as any;
    return live as SitePage;
  });

/**
 * Admin-only: returns the page with its draft merged over the live row,
 * so the preview reflects unpublished edits.
 */
export const getPageDraft = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data, context }): Promise<SitePage | null> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const { data: row } = await context.supabase.from("site_pages").select("*").eq("slug", data.slug).maybeSingle();
    if (!row) return null;
    return mergeDraft(row);
  });

export const listPages = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<SitePageAdmin[]> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const { data } = await context.supabase
      .from("site_pages")
      .select("*")
      .is("deleted_at", null)
      .order("slug");
    return (data ?? []) as SitePageAdmin[];
  });

function builderBlockCount(extra: unknown): number | null {
  const extraRecord =
    extra && typeof extra === "object" ? (extra as Record<string, unknown>) : null;
  const builder = extraRecord?.builder;
  const builderRecord =
    builder && typeof builder === "object" ? (builder as Record<string, unknown>) : null;
  const blocks = builderRecord?.blocks;
  return Array.isArray(blocks) ? blocks.length : null;
}

export const getAdminPage = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data, context }): Promise<SitePageAdmin | null> => {
    console.info("[PageBuilderLoader] server:fetch:start", { slug: data.slug });
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { data: row, error } = await context.supabase
      .from("site_pages")
      .select("*")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) {
      console.info("[PageBuilderLoader] server:fetch:error", {
        slug: data.slug,
        message: error.message,
      });
      throw new Error(error.message);
    }

    const rowRecord = row as { draft?: { extra?: unknown } | null; extra?: unknown } | null;

    console.info("[PageBuilderLoader] server:fetch:success", {
      slug: data.slug,
      found: !!row,
      draftBlockCount: builderBlockCount(rowRecord?.draft?.extra),
      liveBlockCount: builderBlockCount(rowRecord?.extra),
    });

    return (row ?? null) as SitePageAdmin | null;
  });

/** Save edits into the row's draft jsonb. Does not change the live page. */
export const upsertPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: Partial<SitePage> & { slug: string }) => input)
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const draft = pickDraft(data);
    const updates: any = { draft };
    // is_published is a live toggle, applied immediately (not staged).
    if (typeof data.is_published === "boolean") updates.is_published = data.is_published;
    const { error } = await (context.supabase as any)
      .from("site_pages")
      .update(updates)
      .eq("slug", data.slug);
    if (error) throw new Error(error.message);
    // Record a snapshot of the saved draft (merged over live) for history.
    if (Object.keys(draft).length > 0) {
      const { data: row } = await context.supabase
        .from("site_pages")
        .select("*")
        .eq("slug", data.slug)
        .maybeSingle();
      if (row) {
        const merged = { ...(row as any), ...draft };
        await recordVersion(context.supabase, data.slug, "draft_save", snapshotFromRow(merged), null);
      }
    }
    return { ok: true };
  });

export type PageVersion = {
  id: string;
  slug: string;
  kind: HistoryKind;
  snapshot: Partial<SitePage> & Record<string, any>;
  note: string | null;
  created_at: string;
  created_by: string | null;
};

/** Build a snapshot object from a live row (or merged row). */
function snapshotFromRow(row: any): Record<string, any> {
  const snap: Record<string, any> = { is_published: row.is_published };
  for (const k of DRAFT_FIELDS) snap[k] = row[k];
  return snap;
}

async function recordVersion(
  supabase: any,
  slug: string,
  kind: PageVersion["kind"],
  snapshot: Record<string, any>,
  note: string | null,
) {
  await supabase.from("site_page_versions").insert({ slug, kind, snapshot, note });
  // Retention: keep everything from the last 30 days, then one end-of-day
  // snapshot per page per day for a year (best effort — never blocks a save).
  try {
    await supabase.rpc("compact_site_page_versions");
  } catch {
    /* retention is maintenance-only */
  }
}

async function assertAdmin(context: any) {
  const { data: isAdmin } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (!isAdmin) throw new Error("Forbidden");
}

/** Run the publish checklist against a (live-merged) page row. */
function checkRow(row: any): PublishCheck {
  return validatePageForPublish({
    slug: row.slug,
    title: row.title,
    meta_description: row.meta_description,
    hero_title: row.hero_title,
    hero_lead: row.hero_lead,
    body_md: row.body_md,
    use_custom_body: row.use_custom_body,
    is_published: row.is_published,
    extra: row.extra,
    is_custom: row.is_custom,
  });
}

/** Throw with every blocking reason listed, so the UI can show them all. */
function assertPublishable(row: any) {
  const check = checkRow(row);
  if (!check.ok) {
    throw new Error(
      `Publish blocked: ${check.errors.map((e: PublishIssue) => e.message).join(" ")}`,
    );
  }
}




/** Promote the draft into the live row and clear it. Records a publish snapshot. */
export const publishPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string; acknowledgeWarnings?: boolean }) => input)
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const { data: row, error: readErr } = await context.supabase
      .from("site_pages")
      .select("*")
      .eq("slug", data.slug)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (!row) throw new Error("Page not found");
    const draft = (row as any).draft ?? {};
    const updates: any = { draft: null };
    for (const k of DRAFT_FIELDS) {
      if (k in draft) updates[k] = draft[k];
    }
    // Safeguard: never let broken content or SEO reach the live site.
    assertPublishable({ ...(row as any), ...updates });
    const { error } = await (context.supabase as any)
      .from("site_pages")
      .update(updates)
      .eq("slug", data.slug);
    if (error) throw new Error(error.message);
    const merged = { ...(row as any), ...updates };
    await recordVersion(context.supabase, data.slug, "publish", snapshotFromRow(merged), null);
    return { ok: true };
  });

/** Throw away unpublished edits. */
export const discardDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const { error } = await (context.supabase as any)
      .from("site_pages")
      .update({ draft: null })
      .eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listPageVersions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data, context }): Promise<PageVersion[]> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const { data: rows, error } = await context.supabase
      .from("site_page_versions" as any)
      .select("*")
      .eq("slug", data.slug)
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return (rows ?? []) as unknown as PageVersion[];
  });

/**
 * Restore a previous version. Default behavior: load its fields into the draft
 * (live page untouched). Pass mode: "publish" to additionally publish it
 * immediately. A pre-restore snapshot of the current live row is recorded first
 * so the action is reversible.
 */
export const restorePageVersion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { versionId: string; mode?: "draft" | "publish" }) => input)
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const mode = data.mode ?? "draft";
    const { data: ver, error: vErr } = await context.supabase
      .from("site_page_versions" as any)
      .select("*")
      .eq("id", data.versionId)
      .maybeSingle();
    if (vErr) throw new Error(vErr.message);
    if (!ver) throw new Error("Version not found");
    const slug = (ver as any).slug as string;
    const snap = (ver as any).snapshot as Record<string, any>;

    // Always snapshot the current live row first as a safety copy.
    const { data: liveRow } = await context.supabase
      .from("site_pages")
      .select("*")
      .eq("slug", slug)
      .maybeSingle();
    if (liveRow) {
      await recordVersion(
        context.supabase,
        slug,
        "pre_restore",
        snapshotFromRow(liveRow),
        `Auto snapshot before restoring ${data.versionId}`,
      );
    }

    const restoredDraft = pickDraft(snap as Partial<SitePage>);
    if (mode === "publish") {
      const updates: any = { draft: null, ...restoredDraft };
      if (typeof snap.is_published === "boolean") updates.is_published = snap.is_published;
      assertPublishable({ ...(liveRow as any), ...updates });
      const { error } = await (context.supabase as any).from("site_pages").update(updates).eq("slug", slug);
      if (error) throw new Error(error.message);
      const merged = { ...(liveRow as any), ...updates };
      await recordVersion(context.supabase, slug, "publish", snapshotFromRow(merged), `Restored from ${data.versionId}`);
    } else {
      const { error } = await (context.supabase as any)
        .from("site_pages")
        .update({ draft: restoredDraft })
        .eq("slug", slug);
      if (error) throw new Error(error.message);
    }
    return { ok: true, slug };
  });

/**
 * One-click rollback: republish the most recent published version that differs
 * from what's live right now. A pre-restore snapshot is taken first, so the
 * rollback itself is reversible.
 */
export const rollbackToLastPublish = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data, context }): Promise<{ ok: true; restoredFrom: string }> => {
    await assertAdmin(context);
    const { data: liveRow } = await context.supabase
      .from("site_pages")
      .select("*")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!liveRow) throw new Error("Page not found");
    const liveSnap = JSON.stringify(snapshotFromRow(liveRow));

    const { data: versions, error } = await context.supabase
      .from("site_page_versions" as any)
      .select("*")
      .eq("slug", data.slug)
      .eq("kind", "publish")
      .order("created_at", { ascending: false })
      .limit(20);
    if (error) throw new Error(error.message);

    const target = ((versions ?? []) as any[]).find(
      (v) => JSON.stringify(pickSnapshotForCompare(v.snapshot)) !== liveSnap,
    );
    if (!target) throw new Error("No earlier published version to roll back to.");

    await recordVersion(
      context.supabase,
      data.slug,
      "pre_restore",
      snapshotFromRow(liveRow),
      "Auto snapshot before rollback",
    );

    const restored = pickDraft(target.snapshot as Partial<SitePage>);
    const updates: any = { draft: null, ...restored };
    if (typeof target.snapshot.is_published === "boolean") {
      updates.is_published = target.snapshot.is_published;
    }
    assertPublishable({ ...(liveRow as any), ...updates });
    const { error: upErr } = await (context.supabase as any)
      .from("site_pages")
      .update(updates)
      .eq("slug", data.slug);
    if (upErr) throw new Error(upErr.message);
    await recordVersion(
      context.supabase,
      data.slug,
      "publish",
      snapshotFromRow({ ...(liveRow as any), ...updates }),
      `Rolled back to the version from ${new Date(target.created_at).toISOString()}`,
    );
    return { ok: true, restoredFrom: target.created_at as string };
  });

/** Normalise a stored snapshot so it can be compared to the live row. */
function pickSnapshotForCompare(snapshot: any): Record<string, any> {
  const snap: Record<string, any> = { is_published: snapshot?.is_published };
  for (const k of DRAFT_FIELDS) snap[k] = snapshot?.[k];
  return snap;
}

export function pageQueryOptions(slug: string) {
  return queryOptions({
    queryKey: ["site_page", slug],
    queryFn: () => getPage({ data: { slug } }),
    staleTime: 30_000,
  });
}

export function pageDraftQueryOptions(slug: string) {
  return queryOptions({
    queryKey: ["site_page_draft", slug],
    queryFn: () => getPageDraft({ data: { slug } }),
    staleTime: 0,
  });
}


export type PendingDraftSummary = {
  slug: string;
  title: string;
  changedFields: string[];
  updated_at: string;
};

/** Pages that currently have unpublished edits staged in their draft. */
export const listPendingDrafts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<PendingDraftSummary[]> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    const { data, error } = await context.supabase
      .from("site_pages")
      .select("*")
      .not("draft", "is", null)
      .is("deleted_at", null)
      .order("slug");
    if (error) throw new Error(error.message);
    return (data ?? [])
      .filter((row: any) => row.draft && Object.keys(row.draft).length > 0)
      .map((row: any) => ({
        slug: row.slug,
        title: row.draft?.title ?? row.title ?? row.slug,
        changedFields: diffSnapshots(snapshotFromRow(row), { ...snapshotFromRow(row), ...row.draft }),
        updated_at: row.updated_at,
      }));
  });

/** Publish every page that has a pending draft. Returns the slugs published. */
export const publishAllPending = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ published: string[]; skipped: { slug: string; reasons: string[] }[] }> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    const { data, error } = await context.supabase
      .from("site_pages")
      .select("*")
      .not("draft", "is", null)
      .is("deleted_at", null);
    if (error) throw new Error(error.message);
    const published: string[] = [];
    const skipped: { slug: string; reasons: string[] }[] = [];
    for (const row of (data ?? []) as any[]) {
      const draft = row.draft ?? {};
      if (!Object.keys(draft).length) continue;
      const updates: any = { draft: null };
      for (const k of DRAFT_FIELDS) {
        if (k in draft) updates[k] = draft[k];
      }
      // A broken page is skipped, not fatal — the rest of the batch still ships.
      const check = checkRow({ ...row, ...updates });
      if (!check.ok) {
        skipped.push({ slug: row.slug, reasons: check.errors.map((e) => e.message) });
        continue;
      }
      const { error: upErr } = await (context.supabase as any)
        .from("site_pages")
        .update(updates)
        .eq("slug", row.slug);
      if (upErr) throw new Error(upErr.message);
      await recordVersion(
        context.supabase,
        row.slug,
        "publish",
        snapshotFromRow({ ...row, ...updates }),
        "Published via publish-all",
      );
      published.push(row.slug);
    }
    return { published, skipped };
  });

/** Version history for a page, grouped into day buckets with change summaries. */
export const listPageVersionsByDay = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data, context }): Promise<HistoryDay[]> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    const { data: rows, error } = await context.supabase
      .from("site_page_versions" as any)
      .select("*")
      .eq("slug", data.slug)
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return groupVersionsByDay((rows ?? []) as any[]);
  });

/* ------------------------------------------------------------------ *
 * Custom pages: brand-new pages an admin creates from the admin UI.
 * They live entirely in the database and are served by the catch-all
 * route (src/routes/$slug.tsx), so publishing one needs no code deploy.
 * ------------------------------------------------------------------ */

export type CustomPageSummary = { slug: string; title: string; is_published: boolean; updated_at: string };

function publicClient() {
  return createClient<Database>(process.env["SUPABASE_URL"]!, process.env["SUPABASE_PUBLISHABLE_KEY"]!, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

/** Public: a published, admin-created page by slug (null when it doesn't exist). */
export const getCustomPage = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data }): Promise<SitePage | null> => {
    const { data: row } = await publicClient()
      .from("site_pages")
      .select("*")
      .eq("slug", data.slug)
      .eq("is_custom", true)
      .eq("is_published", true)
      .is("deleted_at", null)
      .maybeSingle();
    if (!row) return null;
    const { draft: _drop, ...live } = row as any;
    return live as SitePage;
  });

/** Public: every published custom page (used by the sitemap). */
export const listPublishedCustomPages = createServerFn({ method: "GET" }).handler(
  async (): Promise<CustomPageSummary[]> => {
    const { data } = await publicClient()
      .from("site_pages")
      .select("slug, title, is_published, updated_at")
      .eq("is_custom", true)
      .eq("is_published", true)
      .is("deleted_at", null)
      .order("slug");
    return (data ?? []) as CustomPageSummary[];
  },
);

/** Admin: every custom page, published or not. */
export const listCustomPages = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<CustomPageSummary[]> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    const { data, error } = await context.supabase
      .from("site_pages")
      .select("slug, title, is_published, updated_at")
      .eq("is_custom", true)
      .is("deleted_at", null)
      .order("slug");
    if (error) throw new Error(error.message);
    return (data ?? []) as CustomPageSummary[];
  });

/** Admin: create a brand-new database-backed page. Goes live on publish. */
export const createCustomPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string; title: string }) => input)
  .handler(async ({ data, context }): Promise<{ slug: string }> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const slug = data.slug.trim().toLowerCase().replace(/^\/+|\/+$/g, "");
    const slugIssues = validateSlug(slug);
    if (slugIssues.length) throw new Error(slugIssues.map((i) => i.message).join(" "));
    const title = data.title.trim() || slug;

    const { data: existing } = await context.supabase
      .from("site_pages")
      .select("slug")
      .eq("slug", slug)
      .maybeSingle();
    if (existing) throw new Error(`A page with the address /${slug} already exists.`);

    const { error } = await (context.supabase as any).from("site_pages").insert({
      slug,
      title,
      meta_description: "",
      hero_eyebrow: "",
      hero_title: title,
      hero_lead: "",
      body_md: `# ${title}\n\nStart writing…`,
      use_custom_body: true,
      is_published: false,
      is_custom: true,
      extra: {},
    });
    if (error) throw new Error(error.message);
    return { slug };
  });

/** Admin: delete a custom page (coded routes can never be deleted here). */
export const deleteCustomPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    const { data: row } = await context.supabase
      .from("site_pages")
      .select("slug, is_custom")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!row) throw new Error("Page not found");
    if (!(row as any).is_custom) throw new Error("Only admin-created pages can be deleted.");

    // Soft delete: snapshot the page, then hide it. Restorable for 30 days.
    const { data: full } = await context.supabase
      .from("site_pages")
      .select("*")
      .eq("slug", data.slug)
      .maybeSingle();
    if (full) {
      await recordVersion(
        context.supabase,
        data.slug,
        "pre_delete",
        snapshotFromRow(full),
        "Snapshot taken before deleting the page",
      );
    }
    const { error } = await (context.supabase as any)
      .from("site_pages")
      .update({ deleted_at: new Date().toISOString(), is_published: false })
      .eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export type DeletedPageSummary = { slug: string; title: string; deleted_at: string };

/** Admin: custom pages in the recycle bin (kept 30 days). */
export const listDeletedCustomPages = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<DeletedPageSummary[]> => {
    await assertAdmin(context);
    const { data, error } = await context.supabase
      .from("site_pages")
      .select("slug, title, deleted_at")
      .eq("is_custom", true)
      .not("deleted_at", "is", null)
      .order("deleted_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as DeletedPageSummary[];
  });

/** Admin: bring a deleted custom page back (unpublished, so you can review it). */
export const restoreDeletedPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    await assertAdmin(context);
    const { error } = await (context.supabase as any)
      .from("site_pages")
      .update({ deleted_at: null })
      .eq("slug", data.slug)
      .eq("is_custom", true);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Admin: permanently remove a page already in the recycle bin. */
export const purgeDeletedPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    await assertAdmin(context);
    const { data: row } = await context.supabase
      .from("site_pages")
      .select("slug, is_custom, deleted_at")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!row) throw new Error("Page not found");
    if (!(row as any).is_custom || !(row as any).deleted_at) {
      throw new Error("Only deleted admin-created pages can be removed permanently.");
    }
    const { error } = await context.supabase.from("site_pages").delete().eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export function customPageQueryOptions(slug: string) {
  return queryOptions({
    queryKey: ["custom_page", slug],
    queryFn: () => getCustomPage({ data: { slug } }),
    staleTime: 30_000,
  });
}
